import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  contaReceberSchema, preverVencimentos, type ContaReceberFormData,
} from "../schemas/contaReceberSchema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PessoaSearchCombobox } from "@/components/PessoaSearchCombobox";
import { usePlanoContas } from "@/features/contabil/hooks/usePlanoContas";
import { useTiposDespesas } from "@/features/financeiro/hooks/useCadastrosFinanceiros";

interface ContaReceberFormProps {
  onSubmit: (data: ContaReceberFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  defaultValues?: Partial<ContaReceberFormData>;
  /** Rótulo do botão de confirmação (edição usa "Salvar alterações"). */
  submitLabel?: string;
  /**
   * Trava os campos de PARCELAMENTO (valor, quantidade, vencimento, dias, dia
   * fixo). Usado na edição de um título que já tem recebimento: mudar o
   * parcelamento regeraria o carnê e apagaria o histórico de baixa (o backend
   * recusa). A classificação e os metadados continuam editáveis.
   */
  bloquearParcelamento?: boolean;
}

// Radix Select não aceita value="" — sentinela para "não classificado".
const NENHUM = "__none__";

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const ContaReceberForm = ({
  onSubmit, onCancel, isLoading, defaultValues, submitLabel,
  bloquearParcelamento = false,
}: ContaReceberFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ContaReceberFormData>({
    resolver: zodResolver(contaReceberSchema),
    defaultValues: { qtdParcelas: 1, diaFixo: false, ...defaultValues },
  });

  const { plano: planoContas = [] } = usePlanoContas();
  const { tiposDespesas = [] } = useTiposDespesas();

  // Pré-visualização do carnê enquanto o usuário digita — o servidor recalcula
  // com a mesma regra ao gravar; isto é só para conferência.
  const [dtVencimento, qtdParcelas, valorParcela, diasParcelas, diaFixo] = useWatch({
    control,
    name: ["dtVencimento", "qtdParcelas", "valorParcela", "diasParcelas", "diaFixo"],
  });

  const qtd = Number(qtdParcelas) || 1;
  const valor = Number(valorParcela) || 0;
  const vencimentos = preverVencimentos(dtVencimento ?? "", qtd, diasParcelas, !!diaFixo);
  const total = valor * qtd;

  // Só contas ANALÍTICAS recebem lançamento (tipoContaId === 1); sintéticas agrupam.
  const contasLancaveis = planoContas.filter((c) => c.tipoContaId !== 0);

  const travado = bloquearParcelamento || isLoading;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
      <div className="grid gap-2">
        <Label>Cliente *</Label>
        <Controller
          name="clienteId"
          control={control}
          render={({ field }) => (
            <PessoaSearchCombobox
              value={field.value}
              onChange={field.onChange}
              filter="cliente"
              placeholder="Buscar cliente..."
              disabled={isLoading}
            />
          )}
        />
        {errors.clienteId && (
          <p className="text-sm text-destructive">{errors.clienteId.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="dtEmissao">Data de Emissão *</Label>
          <Input id="dtEmissao" type="date" {...register("dtEmissao")} disabled={isLoading} />
          {errors.dtEmissao && (
            <p className="text-sm text-destructive">{errors.dtEmissao.message}</p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="dtVencimento">1º Vencimento *</Label>
          <Input id="dtVencimento" type="date" {...register("dtVencimento")} disabled={travado} />
          {errors.dtVencimento && (
            <p className="text-sm text-destructive">{errors.dtVencimento.message}</p>
          )}
        </div>
      </div>

      {bloquearParcelamento && (
        <p className="text-xs text-muted-foreground -mt-2">
          Este título já tem recebimento: valor, parcelas e vencimento ficam
          bloqueados. Você pode corrigir a classificação e os dados do documento.
        </p>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="valorParcela">Valor da Parcela (R$) *</Label>
          <Input
            id="valorParcela"
            type="number"
            step="0.01"
            placeholder="0,00"
            {...register("valorParcela")}
            disabled={travado}
          />
          <p className="text-xs text-muted-foreground">
            Valor de UMA parcela — o título é este valor × a quantidade.
          </p>
          {errors.valorParcela && (
            <p className="text-sm text-destructive">{errors.valorParcela.message}</p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="numDocumento">Nº Documento</Label>
          <Input id="numDocumento" placeholder="000000" {...register("numDocumento")} disabled={isLoading} />
          {errors.numDocumento && (
            <p className="text-sm text-destructive">{errors.numDocumento.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="qtdParcelas">Qtd. Parcelas</Label>
          <Input
            id="qtdParcelas"
            type="number"
            min={1}
            max={360}
            {...register("qtdParcelas")}
            disabled={travado}
          />
          {errors.qtdParcelas && (
            <p className="text-sm text-destructive">{errors.qtdParcelas.message}</p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="diasParcelas">Dias entre parcelas</Label>
          <Input
            id="diasParcelas"
            placeholder="30 ou 7,14,28"
            {...register("diasParcelas")}
            disabled={travado}
          />
          <p className="text-xs text-muted-foreground">
            Dias a partir do 1º vencimento. Vazio = 30 em 30.
          </p>
          {errors.diasParcelas && (
            <p className="text-sm text-destructive">{errors.diasParcelas.message}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Controller
          name="diaFixo"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="diaFixo"
              checked={!!field.value}
              onCheckedChange={field.onChange}
              disabled={travado}
            />
          )}
        />
        <Label htmlFor="diaFixo" className="font-normal cursor-pointer">
          Dia fixo (manter o dia do mês do 1º vencimento)
        </Label>
      </div>

      {/* ── Classificação contábil (editável mesmo com recebimento) ── */}
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>Conta contábil</Label>
          <Controller
            name="contaContabilId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ? String(field.value) : NENHUM}
                onValueChange={(v) => field.onChange(v === NENHUM ? undefined : Number(v))}
                disabled={isLoading}
              >
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NENHUM}>— Não classificada —</SelectItem>
                  {contasLancaveis.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.codigoPlanoConta ? `${c.codigoPlanoConta} — ` : ""}{c.descricaoConta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="grid gap-2">
          <Label>Tipo de despesas</Label>
          <Controller
            name="tipoDespesasId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ? String(field.value) : NENHUM}
                onValueChange={(v) => field.onChange(v === NENHUM ? undefined : Number(v))}
                disabled={isLoading}
              >
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NENHUM}>— Não classificado —</SelectItem>
                  {tiposDespesas.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="lancamento">Descrição</Label>
        <Input
          id="lancamento"
          placeholder="Ex.: Venda a prazo, contrato mensal…"
          {...register("lancamento")}
          disabled={isLoading}
        />
      </div>

      {/* Prévia do carnê: só quando o parcelamento está editável. */}
      {!bloquearParcelamento && vencimentos.length > 0 && valor > 0 && (
        <div className="rounded-md border p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {vencimentos.length} parcela(s) de {brl(valor)}
            </span>
            <span className="text-sm font-semibold">Total: {brl(total)}</span>
          </div>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {vencimentos.map((d, i) => (
                  <tr key={i} className="border-t first:border-t-0">
                    <td className="py-1 text-muted-foreground w-16">{i + 1}/{vencimentos.length}</td>
                    <td className="py-1">{d.toLocaleDateString("pt-BR")}</td>
                    <td className="py-1 text-right">{brl(valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Salvando..." : (submitLabel ?? "Salvar")}
        </Button>
      </div>
    </form>
  );
};
